/**
 * IndexedDB-backed offline queue.
 *
 * Why: inspectors save inspections at the back of yards on poor signal. Saves
 * that fail with a network error get stashed locally and flushed when the
 * browser fires `online` (or on app load).
 *
 * Storage: IndexedDB (photo-heavy payloads exceeded the localStorage quota —
 * QuotaExceededError crashed inspections in the field). A legacy localStorage
 * queue, if present, is migrated in on first read.
 *
 * Keyed by inspection id so only the latest pending state per inspection ships.
 */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { kvGet, kvSet } from "@/lib/localStore";
import { getLocalInspections, removeLocalInspection, mapLocalId } from "@/lib/offlineCache";
import { slimPayload } from "@/lib/photoOffload";

const KEY = "psc_offline_queue_v1";
const PARK_KEY = "psc_offline_parked_v1";

const readQ = async () => {
  let q = (await kvGet(KEY)) || {};
  // One-time migration of any legacy localStorage queue
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      q = { ...JSON.parse(raw), ...q };
      localStorage.removeItem(KEY);
      await kvSet(KEY, q);
    }
  } catch { /* noop */ }
  return q;
};
const writeQ = (q) => kvSet(KEY, q);

export const queueInspectionSave = async (id, payload) => {
  try {
    const q = await readQ();
    q[id] = { payload, at: Date.now() };
    await writeQ(q);
  } catch { /* local backup must never throw */ }
};

export const clearInspectionFromQueue = async (id) => {
  try {
    const q = await readQ();
    if (q[id]) { delete q[id]; await writeQ(q); }
  } catch { /* noop */ }
};

export const getQueuedSaves = async () => {
  try { return await readQ(); } catch { return {}; }
};

export const isNetworkError = (err) => {
  if (!err) return false;
  // axios marks network failures with no response
  if (err.message === "Network Error" || !err.response) return true;
  return false;
};

/** Create any offline-created inspections on the server.
 *  Returns { created, failed: [{ id, title, error }] }. Local copies are never
 *  dropped on failure — field data is sacred — but permanent errors are now
 *  surfaced so a stuck inspection can't silently hide forever (it used to just
 *  sit on the device, inflating the phone's count vs the server's). */
const syncLocalInspections = async () => {
  let created = 0;
  const failed = [];
  for (const li of await getLocalInspections()) {
    try {
      // module MUST travel with the create — omitting it silently filed
      // every synced inspection under the default discipline.
      let body = {
        title: li.title || "",
        items: li.items || {},
        template_id: li.template_id || null,
        module: li.module || undefined,
      };
      try { body = { ...body, ...(await slimPayload(body, li.id)) }; } catch { /* keep inline */ }
      const r = await api.post("/inspections", body);
      if (li.manual_outcome) {
        await api.put(`/inspections/${r.data.id}`, {
          title: li.title || "", items: li.items || {},
          manual_outcome: li.manual_outcome, manual_outcome_reason: li.manual_outcome_reason || "",
        }).catch(() => {});
      }
      await mapLocalId(li.id, r.data.id);
      await removeLocalInspection(li.id);
      created += 1;
    } catch (err) {
      if (isNetworkError(err)) break; // still offline — retry on the next flush
      // Permanent error: keep the local copy, but report why so the UI can show it.
      failed.push({
        id: li.id,
        title: li.title || "Untitled inspection",
        error: err?.response?.data?.detail || err?.response?.status || err?.message || "Unknown error",
      });
    }
  }
  return { created, failed };
};

/** Flush every pending save + device-only inspections. Returns
 *  { flushed, created, failed } — failed lists device-only inspections the
 *  server rejected (with the reason) so callers can surface them. */
export const flushOfflineQueue = async () => {
  const local = await syncLocalInspections();
  let flushed = 0;
  const q = await readQ();
  const ids = Object.keys(q);
  for (const id of ids) {
    try {
      // Upload big photos individually first — huge PUT bodies get rejected
      // by the proxy and masquerade as network errors, wedging the queue.
      try { q[id].payload = await slimPayload(q[id].payload, id); } catch { /* keep inline */ }
      await api.put(`/inspections/${id}`, q[id].payload);
      delete q[id];
      flushed += 1;
    } catch (err) {
      if (!isNetworkError(err)) {
        // Permanent failure: never silently drop field data. Retry a few
        // times (photo offload may fix it), then park it for recovery.
        q[id].attempts = (q[id].attempts || 0) + 1;
        if (q[id].attempts >= 5) {
          try {
            const parked = (await kvGet(PARK_KEY)) || {};
            parked[id] = { ...q[id], parked_at: Date.now(), error: err?.response?.status || err?.message };
            await kvSet(PARK_KEY, parked);
          } catch { /* keep in queue if parking fails */ }
          delete q[id];
        }
      }
    }
  }
  await writeQ(q);
  return { flushed: flushed + local.created, created: local.created, failed: local.failed };
};

export const useOfflineSync = () => {
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const refresh = () => { readQ().then((q) => setPending(Object.keys(q).length)); };
  const flushing = useRef(false);

  const tryFlush = async () => {
    if (flushing.current || !navigator.onLine) return;
    flushing.current = true;
    try {
      const n = await flushOfflineQueue();
      if (n.flushed > 0) refresh();
    } finally { flushing.current = false; }
  };

  useEffect(() => {
    refresh();
    const onOnline = () => { setOnline(true); tryFlush(); };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    // poll every 20s in case service worker missed online event
    const t = setInterval(tryFlush, 20000);
    // first-load flush
    tryFlush();
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { online, pending, refresh };
};
